import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { CollegeLogo } from '../../components/CollegeSearchCombobox';

const CollegeGlobe = React.lazy(() => import('../../components/college/CollegeGlobe'));

const PLAN_LABELS = {
  ED: 'Early Decision',
  ED_II: 'Early Decision II',
  EA: 'Early Action',
  REA: 'Restrictive Early Action',
  RD: 'Regular Decision',
  ROLLING: 'Rolling',
  UCAS: 'UCAS Main',
  OTHER: 'Other',
};

const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

const dateShort = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const monthYear = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

function PlanBadge({ plan }) {
  const solid = plan === 'ED';
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: 999,
        background: solid ? 'var(--college-ink)' : 'transparent',
        color: solid ? 'var(--college-panel)' : 'var(--college-ink)',
        border: solid ? 'none' : '1px solid var(--college-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      {PLAN_SHORT[plan] || plan}
    </span>
  );
}

function FitBadge({ fit, fitColor, fitBg }) {
  if (!fit) {
    return <span style={{ fontSize: 11, color: 'var(--college-muted-2)' }}>—</span>;
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 12px',
        borderRadius: 999,
        background: fitBg,
        color: fitColor,
        whiteSpace: 'nowrap',
      }}
    >
      {fit}
    </span>
  );
}

// Group applications into one timeline segment per unique deadline month,
// sorted chronologically, plus a "Today" marker inserted in its true
// chronological position.
function buildSeasonSegments(applications) {
  const today = new Date();
  const groups = new Map();
  applications.forEach((app) => {
    const d = new Date(app.deadline);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!groups.has(key)) groups.set(key, { date: d, names: [] });
    groups.get(key).names.push(app.collegeName);
  });
  const monthSegments = Array.from(groups.values()).map((g) => ({
    date: g.date,
    isToday: false,
    dateLabel: monthYear(g.date),
    label: g.names.length > 2 ? `${g.names.slice(0, 2).join(', ')} +${g.names.length - 2}` : g.names.join(', '),
  }));
  const todaySegment = {
    date: today,
    isToday: true,
    dateLabel: dateShort(today),
    label: 'Today',
  };
  return [...monthSegments, todaySegment].sort((a, b) => a.date - b.date);
}

export default function CollegeOverviewPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get('/college-applications'), api.get('/student-profile')])
      .then(([appsRes, profileRes]) => {
        if (!alive) return;
        setApplications(appsRes.data.applications || []);
        setProfile(profileRes.data.profile || null);
      })
      .catch(() => {
        if (alive) toast.error('Failed to load your college overview');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [api]);

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>Loading overview…</p>
      </div>
    );
  }

  const soonest = applications[0] || null;
  const planDisplay = soonest ? (soonest.planLabel || PLAN_LABELS[soonest.plan] || soonest.plan) : null;

  const pins = applications
    .filter((a) => a.lat != null && a.lng != null)
    .map((a) => ({
      id: a.id,
      lat: a.lat,
      lng: a.lng,
      label: a.collegeName,
      color: a.fitPin || '#D8A94C',
    }));

  const heroBorder = 'rgba(241,233,216,0.18)';
  const seasonSegments = applications.length ? buildSeasonSegments(applications) : [];

  return (
    <div>
      <style>{`
        @media (max-width: 860px) {
          .college-overview-hero-grid { grid-template-columns: 1fr !important; }
          .college-overview-hero-grid > div:last-child { margin-top: 32px; }
        }
      `}</style>

      {/* ── Dark hero band — full bleed ───────────────────────── */}
      <div style={{ background: 'var(--college-ink)', color: 'var(--college-panel)' }}>
        <div
          className="college-overview-hero-grid animate-fade-up"
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '48px 32px 40px',
            display: 'grid',
            gridTemplateColumns: '5fr 7fr',
            gap: 48,
            alignItems: 'start',
          }}
        >
          {/* Left column */}
          <div>
            {soonest ? (
              <>
                <p className="label-editorial" style={{ color: 'rgba(241,233,216,0.6)', margin: '0 0 10px' }}>Next deadline</p>
                <div className="font-serif-editorial" style={{ fontSize: 112, lineHeight: 0.95, fontWeight: 400, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  {soonest.days}
                  <span style={{ fontSize: 40, fontStyle: 'italic', marginLeft: 10 }}> days</span>
                </div>
                <p style={{ margin: '16px 0 0', fontSize: 15, color: 'rgba(241,233,216,0.75)', lineHeight: 1.5 }}>
                  until{' '}
                  <Link
                    to={`/college/${soonest.id}`}
                    style={{ color: 'var(--college-panel)', textDecoration: 'underline', textDecorationColor: 'var(--college-gold)', textUnderlineOffset: 4 }}
                  >
                    {soonest.collegeName}
                  </Link>{' '}
                  {planDisplay} — <span className="font-mono-editorial">{dateShort(soonest.deadline)}</span>
                </p>

                {/* Profile numbers row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginTop: 30, paddingTop: 18, borderTop: `1px solid ${heroBorder}`, flexWrap: 'wrap' }}>
                  {[
                    { label: 'SAT', value: profile?.satComposite },
                    { label: 'IB', value: profile?.ibPredicted },
                    { label: 'GPA', value: profile?.gpa },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="label-editorial" style={{ color: 'rgba(241,233,216,0.5)', margin: '0 0 2px', fontSize: 10 }}>{label}</p>
                      <p className="font-mono-editorial" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                        {value != null && value !== '' ? value : '—'}
                      </p>
                    </div>
                  ))}
                  <Link
                    to="/college/profile"
                    style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--college-gold)', textDecoration: 'none' }}
                  >
                    edit →
                  </Link>
                </div>

                {/* Fit-tier legend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 18 }}>
                  {[
                    { label: 'Reach', color: 'var(--college-reach)' },
                    { label: 'Target', color: 'var(--college-target)' },
                    { label: 'Likely', color: 'var(--college-likely)' },
                  ].map(({ label, color }) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(241,233,216,0.6)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                      {label}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="label-editorial" style={{ color: 'rgba(241,233,216,0.6)', margin: '0 0 14px' }}>Next deadline</p>
                <p className="font-serif-editorial" style={{ fontSize: 40, fontStyle: 'italic', margin: '0 0 14px', lineHeight: 1.2 }}>
                  No schools on your list yet
                </p>
                <p style={{ margin: '0 0 4px', fontSize: 15, color: 'rgba(241,233,216,0.75)' }}>
                  Go add some to start the countdown.
                </p>
                <Link to="/college/list" style={{ display: 'inline-block', marginTop: 10, fontSize: 13, color: 'var(--college-gold)', textDecoration: 'underline' }}>
                  Open My List →
                </Link>
              </>
            )}
          </div>

          {/* Right column — globe */}
          <div>
            <React.Suspense fallback={<div style={{ height: 540, background: 'rgba(241,233,216,0.05)' }} />}>
              <CollegeGlobe pins={pins} onPinClick={(d) => navigate(`/college/${d.id}`)} height={540} />
            </React.Suspense>
            <p style={{ textAlign: 'center', margin: '10px 0 0', fontSize: 12, color: 'rgba(241,233,216,0.45)' }}>
              drag to spin · click a pin to open a school
            </p>
          </div>
        </div>
      </div>

      {/* ── Light content below hero ─────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: '2px solid var(--college-ink)', paddingBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <h2 className="font-serif-editorial" style={{ fontSize: 30, fontStyle: 'italic', margin: 0 }}>The List, abridged</h2>
          <Link to="/college/list" style={{ fontSize: 13, color: 'var(--college-ink-soft)', textDecoration: 'none' }}>
            Open full list →
          </Link>
        </div>

        {applications.length === 0 ? (
          <p style={{ padding: '24px 4px', fontSize: 14, color: 'var(--college-muted)' }}>
            No schools on your list yet — <Link to="/college/list" style={{ color: 'var(--college-ink)', textDecoration: 'underline' }}>go add some</Link>.
          </p>
        ) : (
          <div>
            {applications.slice(0, 5).map((app) => (
              <div
                key={app.id}
                onClick={() => navigate(`/college/${app.id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '16px 4px',
                  borderBottom: '1px solid var(--college-border-soft)',
                  cursor: 'pointer',
                }}
              >
                <CollegeLogo schoolUrl={app.schoolUrl} name={app.collegeName} size={28} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <Link
                    to={`/college/${app.id}`}
                    className="font-serif-editorial"
                    style={{ fontSize: 19, color: 'var(--college-ink)', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {app.collegeName}
                  </Link>
                  {app.city && (
                    <span style={{ fontSize: 12, color: 'var(--college-muted)' }}>
                      {app.city}{app.region ? `, ${app.region}` : ''}
                    </span>
                  )}
                </div>
                <PlanBadge plan={app.plan} />
                <span className="font-mono-editorial" style={{ fontSize: 12, minWidth: 118, textAlign: 'right', color: 'var(--college-ink-soft)' }}>
                  {dateShort(app.deadline)} · {app.days}d
                </span>
                <FitBadge fit={app.fit} fitColor={app.fitColor} fitBg={app.fitBg} />
              </div>
            ))}
          </div>
        )}

        {/* ── The season timeline strip ─────────────────────── */}
        {seasonSegments.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <p className="label-editorial" style={{ margin: '0 0 24px' }}>The season</p>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: 5, left: 0, right: 0, height: 1, background: 'var(--college-border)' }} />
              <div style={{ display: 'flex', gap: 36, overflowX: 'auto', paddingBottom: 6 }}>
                {seasonSegments.map((seg, i) => (
                  <div key={i} style={{ position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 108, paddingTop: 18, flexShrink: 0 }}>
                    <span
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: seg.isToday ? 'var(--college-gold)' : 'var(--college-red)',
                        border: '2px solid var(--college-bg)',
                      }}
                    />
                    <span className="font-mono-editorial" style={{ fontSize: 13, fontWeight: 700, color: 'var(--college-ink)' }}>
                      {seg.dateLabel}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--college-muted)', marginTop: 3 }}>
                      {seg.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
